import CalendarIcon from "@mui/icons-material/Event";
import { IconButton, InputAdornment } from "@mui/material";
import { type DatePickerProps, DatePicker } from "@mui/x-date-pickers";
import { useState } from "react";
import {
  type CommonInputProps,
  FieldTitle,
  useInput,
  useTranslate,
} from "react-admin";

import { DEFAULT_DATE_FORMAT } from "@/constants/date";
import { REACT_ADMIN_VALIDATION_REGEXP } from "@/constants/validation";

import { defaultIsoDateFormat } from "../IsoDateInput/utils/defaultIsoDateFormat";

type BaseDateInputProps = CommonInputProps & {
  datePickerProps?: Partial<DatePickerProps>;
};

export const BaseDateInput = ({
  datePickerProps,
  onBlur,
  onChange,
  label,
  isRequired,
  format = defaultIsoDateFormat,
  helperText,
  ...rest
}: BaseDateInputProps) => {
  const { field, fieldState } = useInput({
    onBlur,
    onChange,
    format,
    ...rest,
  });
  const translate = useTranslate();
  const errorMessage = fieldState.error?.message?.replace(
    REACT_ADMIN_VALIDATION_REGEXP,
    "",
  );
  const [open, setOpen] = useState(false);

  const slotProps = {
    ...(datePickerProps?.slotProps || {}),
    textField: {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment
      variant: "filled" as any,
      ...(datePickerProps?.slotProps?.textField || {}),
      sx: {
        "& .MuiPickersInputBase-root": {
          pr: 0,
        },
        "& .MuiPickersInputBase-sectionsContainer": {
          pt: "21px",
          pb: 0.5,
        },
      },
      error: Boolean(fieldState.error),
      helperText: helperText ?? (errorMessage ? translate(errorMessage) : " "),
      onClick: () => setOpen(true),
      InputProps: {
        endAdornment: (
          <InputAdornment position="end">
            <IconButton onClick={() => setOpen(true)}>
              <CalendarIcon />
            </IconButton>
          </InputAdornment>
        ),
      },
    },
  };

  return (
    <DatePicker
      label={
        <FieldTitle
          label={label}
          source={rest.source}
          isRequired={isRequired}
        />
      }
      format={DEFAULT_DATE_FORMAT}
      {...field}
      {...rest}
      {...datePickerProps}
      open={open}
      onClose={() => setOpen(false)}
      slotProps={slotProps}
    />
  );
};
